import { useState, useEffect, useRef } from 'react';
import { Box, Grid, Skeleton, Alert, Button, Paper } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowBack, ArrowForward, ArrowBackIos } from '@mui/icons-material';
import { useGetInvestmentChecklist } from '@/lib/api/investment-checklist';
import UpgradeWallModal from '@/features/billing/components/UpgradeWallModal.tsx';
import ChecklistStatsHeader from '@/features/investments/checklist/ChecklistStatsHeader';
import ChecklistProgressStepper from '@/features/investments/checklist/ChecklistProgressStepper';
import PhaseTasksList from '@/features/investments/checklist/PhaseTasksList';
import ChecklistAnchorsPanel from '@/features/investments/checklist/ChecklistAnchorsPanel';
import { PHASE_ORDER } from '@/features/investments/checklist/checklist-constants';
import {
  getCurrentPhase,
  isPhaseSkipped,
  getOverdueTasks,
} from '@/features/investments/checklist/checklist-utils';
import OverdueTasksDialog from '@/features/investments/checklist/OverdueTasksDialog';
import { ChecklistPhase, InvestmentChecklistTaskDTO } from '@inversor-hub/types';
import { useInvestorInvestmentAccess } from '@/features/billing/useInvestorInvestmentAccess.ts';

const InvestmentChecklistPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: checklist, isLoading, isError } = useGetInvestmentChecklist(id);
  const {
    isLockedInvestment,
    isLoading: isLoadingAccess,
    upgradeWallInvestmentCount,
    upgradeWallPlansPath,
  } = useInvestorInvestmentAccess();
  const [selectedPhase, setSelectedPhase] = useState<ChecklistPhase>(PHASE_ORDER[0]);
  const [overdueDialogOpen, setOverdueDialogOpen] = useState(false);
  const hasAutoSelectedPhase = useRef(false);

  useEffect(() => {
    if (checklist && !hasAutoSelectedPhase.current) {
      const current = getCurrentPhase(checklist);
      if (current !== 'COMPLETE') {
        setSelectedPhase(current as ChecklistPhase);
      }
      hasAutoSelectedPhase.current = true;
    }
  }, [checklist]);

  const currentPhaseIndex = PHASE_ORDER.indexOf(selectedPhase);

  const canGoPrevious = checklist
    ? PHASE_ORDER.slice(0, currentPhaseIndex).some(
        (phase) => !isPhaseSkipped(checklist.tasks, phase)
      )
    : false;
  const canGoNext = checklist
    ? PHASE_ORDER.slice(currentPhaseIndex + 1).some(
        (phase) => !isPhaseSkipped(checklist.tasks, phase)
      )
    : false;

  const handlePreviousPhase = () => {
    if (!canGoPrevious || !checklist) return;

    let nextIndex = currentPhaseIndex - 1;
    while (nextIndex >= 0) {
      const phase = PHASE_ORDER[nextIndex];
      if (!isPhaseSkipped(checklist.tasks, phase)) {
        setSelectedPhase(phase);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      nextIndex--;
    }
  };

  const handleNextPhase = () => {
    if (!canGoNext || !checklist) return;

    let nextIndex = currentPhaseIndex + 1;
    while (nextIndex < PHASE_ORDER.length) {
      const phase = PHASE_ORDER[nextIndex];
      if (!isPhaseSkipped(checklist.tasks, phase)) {
        setSelectedPhase(phase);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      nextIndex++;
    }
  };

  const handlePhaseClick = (phase: string) => {
    setSelectedPhase(phase as ChecklistPhase);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGoToOverdueTask = (task: InvestmentChecklistTaskDTO) => {
    setSelectedPhase(task.phase);
    setOverdueDialogOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading || isLoadingAccess) {
    return (
      <Box p={3}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={150} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={100} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (isLockedInvestment(id)) {
    return (
      <UpgradeWallModal
        open
        onClose={() => navigate('/inversiones')}
        onViewPlans={() => navigate(upgradeWallPlansPath)}
        investmentCount={upgradeWallInvestmentCount}
      />
    );
  }

  if (isError || !checklist) {
    return (
      <Box p={3}>
        <Alert severity="error">
          No se pudo cargar el checklist de la inversión. Intenta de nuevo más tarde.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/inversiones`)} sx={{ mr: 2 }}>
          Volver
        </Button>
      </Box>

      <ChecklistStatsHeader
        checklist={checklist}
        onViewOverdueTasks={() => setOverdueDialogOpen(true)}
      />
      <ChecklistProgressStepper
        checklist={checklist}
        onPhaseClick={handlePhaseClick}
        selectedPhase={selectedPhase}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Box
            display="flex"
            justifyContent="space-between"
            gap={1}
            mb={3}
            sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIos />}
              onClick={handlePreviousPhase}
              disabled={!canGoPrevious}
            >
              Fase anterior
            </Button>
            <Button
              variant="outlined"
              endIcon={<ArrowForward />}
              onClick={handleNextPhase}
              disabled={!canGoNext}
            >
              Siguiente fase
            </Button>
          </Box>

          <PhaseTasksList
            phase={selectedPhase}
            tasks={checklist.tasks}
            investmentId={checklist.investmentId}
            onPhaseSkipped={handleNextPhase}
          />

          <Paper
            sx={{
              p: 2,
              mt: 3,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIos />}
              onClick={handlePreviousPhase}
              disabled={!canGoPrevious}
            >
              Fase anterior
            </Button>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={handleNextPhase}
              disabled={!canGoNext}
            >
              {canGoNext ? 'Siguiente fase' : 'Completado'}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChecklistAnchorsPanel checklist={checklist} />
        </Grid>
      </Grid>

      <OverdueTasksDialog
        open={overdueDialogOpen}
        onClose={() => setOverdueDialogOpen(false)}
        tasks={getOverdueTasks(checklist.tasks)}
        onGoToTask={handleGoToOverdueTask}
      />
    </Box>
  );
};

export default InvestmentChecklistPage;
